import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Icon } from "@/components/icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { logout } from "@/features/auth/api/authApi";
import { ROUTES } from "@/constants";
import { useI18n } from "@/i18n/useI18n";
import { useUserStore } from "@/store";
import { useCheckoutCartStore } from "@/store/useCheckoutCartStore";

export function Header() {
  const { t } = useI18n();
  const currentUser = useUserStore((state) => state.currentUser);
  const clearSession = useUserStore((state) => state.clearSession);
  const clearSale = useCheckoutCartStore((state) => state.clearSale);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const displayName = currentUser?.fullName ?? t("layout.user.noUser");
  const displayRole = currentUser?.role ?? t("layout.user.noRole");
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSettled: () => {
      clearSale();
      queryClient.clear();
      clearSession();
      navigate(ROUTES.LOGIN, { replace: true });
    },
  });

  return (
    <header className="flex h-[56px] shrink-0 items-center bg-primary">
      {/* Logo Section - Pink background area */}
      <div className="flex h-full w-[200px] items-center gap-2 px-4">
        <img
          src="/logo.webp"
          alt="Mi Alegría logo"
          className="h-8 w-8 object-contain"
        />
        <span className="text-lg font-semibold text-primary-foreground">
          {t("layout.appName")}
        </span>
      </div>

      {/* Main Header Area - Dark background */}
      <div className="flex flex-1 h-full items-center justify-end px-4">
        {/* Right Section */}
        <div className="flex items-center gap-4">
          <button className="relative rounded-full p-2 text-yellow-400 hover:bg-sidebar-accent">
            <Icon icon="bell" className="h-5 w-5" />
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 rounded-md p-1 text-left hover:bg-background/10"
                type="button"
              >
                <img
                  src="/placeholder-user.jpg"
                  alt={t("layout.user.avatarAlt", { name: displayName })}
                  className="h-9 w-9 rounded-full object-cover"
                />
                <div className="min-w-0 leading-tight">
                  <span className="block truncate text-sm font-medium text-primary-foreground">
                    {displayName}
                  </span>
                  <span className="block truncate text-xs capitalize text-primary-foreground/70">
                    {displayRole}
                  </span>
                </div>
                <Icon
                  icon="chevronDown"
                  className="h-4 w-4 text-primary-foreground"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{displayName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                disabled={logoutMutation.isPending}
                onSelect={() => logoutMutation.mutate()}
              >
                {logoutMutation.isPending ? t("layout.loggingOut") : t("layout.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button className="flex h-8 w-8 items-center justify-center rounded-full border border-primary-foreground text-primary-foreground hover:bg-sidebar-accent">
            <Icon icon="plus" className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
